import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Button,
  Avatar,
  IconButton,
  Paper,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Tooltip
} from '@mui/material';
import {
  People,
  CalendarToday,
  Warning,
  Message,
  MonitorHeart,
  Phone,
  VideoCall,
  TrendingUp,
  AccessTime,
  CheckCircle,
  Download
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Pie,
  Cell,
  PieChart,
  Legend
} from 'recharts';
import axios from 'axios';
import { Patient, Appointment } from '../../types';
import { useAuth } from '../../contexts/AuthContext';

// Utility function to fix S3 URL format
const fixS3Url = (url: string | undefined): string | undefined => {
  if (!url) return undefined;

  // Check if it's an S3 URL with regional endpoint
  const s3RegionalPattern = /https:\/\/([^.]+)\.s3\.([^.]+)\.amazonaws\.com\/(.+)/;
  const match = url.match(s3RegionalPattern);

  if (match) {
    const bucketName = match[1];
    const path = match[3];

    // Convert to path-style URL (works better with CORS)
    const fixedUrl = `https://${bucketName}.s3.amazonaws.com/${path}`;
    console.log('🔧 Fixed S3 URL:', url, '->', fixedUrl);
    return fixedUrl;
  }

  // If already in standard format, return as is
  return url;
};

// Utility function to get cache-busted image URL
const getCacheBustedImageUrl = (url: string | undefined, timestamp?: number): string | undefined => {
  if (!url) return undefined;

  const fixedUrl = fixS3Url(url);
  if (!fixedUrl) return undefined;

  const ts = timestamp || Date.now();
  return `${fixedUrl}?t=${ts}`;
};

const COLORS = ['#f44336', '#ff9800', '#4caf50'];

const StatCard = React.memo(({ title, value, icon, color, subtitle }: any) => (
  <Card elevation={2}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography color="textSecondary" gutterBottom variant="h6">
            {title}
          </Typography>
          <Typography variant="h4" component="h2" sx={{ color }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="textSecondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box sx={{ color, opacity: 0.8 }}>
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
));

const DoctorProfileCard = React.memo(({ user, imageUrl }: any) => {
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Avatar
            src={imageUrl}
            imgProps={{
              crossOrigin: "anonymous",
              onError: (e) => {
                console.error('❌ Doctor avatar image failed to load:', imageUrl);
              }
            }}
            sx={{
              width: 80,
              height: 80,
              bgcolor: imageUrl ? 'transparent' : 'primary.main',
              fontSize: '2rem',
              border: imageUrl ? '2px solid #e0e0e0' : 'none'
            }}
          >
            {(!imageUrl && (user?.full_name || user?.username)?.charAt(0)?.toUpperCase()) || 'D'}
          </Avatar>
          <Box>
            <Typography variant="h6" gutterBottom>
              Welcome, Dr. {user?.full_name || user?.username}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {user?.specialization || 'Specialist'} • {user?.hospital_name || 'Hospital'} • {user?.years_of_experience || 'Years of Experience'} Years
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
});

export default function DoctorDashboard() {
  const { user } = useAuth();
  const [myPatients, setMyPatients] = useState<Patient[]>([]);
  const [todayAppointments, setTodayAppointments] = useState<Appointment[]>([]);
  const [criticalPatients, setCriticalPatients] = useState<Patient[]>([]);
  const [updatingAppointment, setUpdatingAppointment] = useState<string | null>(null);

  // Analytics state
  const [healthStatusData, setHealthStatusData] = useState<any[]>([]);
  const [weeklyTrendsData, setWeeklyTrendsData] = useState<any[]>([]);
  const [totalAssignedPatients, setTotalAssignedPatients] = useState(0);
  const [trendsFilter, setTrendsFilter] = useState<string>('heartrate');
  // Analytics state
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [patientsError, setPatientsError] = useState<string | null>(null);
  const [trendsLoading, setTrendsLoading] = useState(false);
  const [trendsError, setTrendsError] = useState<string | null>(null);
  const [downloadingReport, setDownloadingReport] = useState(false);

  const handleBulkDownload = async () => {
    try {
      if (!user?.id) return;
      setDownloadingReport(true);
      const token = localStorage.getItem('token');

      const response = await fetch(`${process.env.REACT_APP_API_URL}/reports/doctor-bulk-weekly`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'accept': 'application/pdf'
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const filename = `Doctor_Weekly_Summary_${new Date().toISOString().split('T')[0]}.pdf`;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        console.error('Failed to download bulk report');
        // Optionally set an error state to show a snackbar
      }
    } catch (error) {
      console.error('Error downloading bulk report:', error);
    } finally {
      setDownloadingReport(false);
    }
  };
  const [statusFilter, setStatusFilter] = useState<string>('heartrate');
  const [statusLoading, setStatusLoading] = useState(false);
  const [stableImageTimestamp] = useState(Date.now());

  // --- Status Calculation Logic (Mirrored from HealthMonitorPage) ---
  const calculateHeartRateStatus = (bpm: number | null): string | null => {
    if (bpm === null) return null;
    if (bpm < 60) return 'Slow';
    if (bpm <= 85) return 'Normal';
    if (bpm <= 100) return 'Elevated';
    if (bpm <= 120) return 'High';
    return 'Very High';
  };

  const calculateBPStatus = (systolic: number | null, diastolic: number | null): string | null => {
    if (!systolic || !diastolic) return null;
    if (systolic > 180 || diastolic > 120) return 'Hypertensive Crisis';
    if (systolic >= 140 || diastolic >= 90) return 'Stage 2 Hypertension';
    if ((systolic >= 130 && systolic <= 139) || (diastolic >= 80 && diastolic <= 89)) return 'Stage 1 Hypertension';
    if ((systolic >= 120 && systolic <= 129) && diastolic < 80) return 'Elevated';
    return 'Normal';
  };

  const calculateBMIStatus = (bmi: number | null): string | null => {
    if (bmi === null) return null;
    if (bmi < 18.5) return 'Underweight';
    if (bmi <= 24.9) return 'Normal';
    if (bmi <= 29.9) return 'Overweight';
    return 'Obese';
  };

  const mapStatusToCategory = (status: string | null): 'critical' | 'warning' | 'normal' => {
    if (!status) return 'normal';

    // Normalize string: lowercase and remove underscores/extra spaces
    const s = status.toLowerCase().replace(/_/g, ' ').trim();

    // Critical
    if (
      ['high', 'very high', 'stage 2 hypertension', 'hypertensive crisis', 'obese', 'critical'].includes(s) ||
      s.includes('stage 2') ||
      s.includes('crisis') ||
      s.includes('obese') ||
      s.includes('critical') ||
      (s.includes('high') && !s.includes('normal'))
    ) return 'critical';

    // Warning
    if (
      ['slow', 'elevated', 'stage 1 hypertension', 'underweight', 'overweight', 'warning'].includes(s) ||
      s.includes('stage 1') ||
      s.includes('elevated') ||
      s.includes('slow') ||
      s.includes('underweight') ||
      s.includes('overweight') ||
      s.includes('warning')
    ) return 'warning';

    return 'normal';
  };

  // Calculate Status Distribution Locally
  useEffect(() => {
    // Removed early return to ensure chart always renders with 0s if empty
    // if (myPatients.length === 0 && !patientsLoading) {
    //   setHealthStatusData([]);
    //   return;
    // }

    let criticalCount = 0;
    let warningCount = 0;
    let normalCount = 0;

    // Create a map of unique patients by ID to handle potential duplicates in the list
    const uniquePatients = new Map();
    myPatients.forEach((patient, index) => {
      // Prioritize patient_id, then id, then _id. 
      // If none exist, fall back to index to ensure we don't lose data, 
      // but this means duplicates won't be caught if IDs are missing.
      const key = patient.patient_id || patient.id || (patient as any)._id || `unknown_${index}`;

      if (!uniquePatients.has(key)) {
        uniquePatients.set(key, patient);
      }
    });

    console.log('📊 Unique Patients count:', uniquePatients.size);

    uniquePatients.forEach((patient: any) => {
      const vitalsObj = patient.latest_vitals || {};

      let status: 'critical' | 'warning' | 'normal' = 'normal';

      if (statusFilter === 'heartrate') {
        const heartData = vitalsObj.heartrate || vitalsObj.heart_rate || vitalsObj.heartRate || {};
        const val = parseInt(heartData.heart_rate);
        const bpm = isNaN(val) ? null : val;

        // Fallback logic: Use pre-calculated status if raw value is missing
        const det = bpm !== null ? calculateHeartRateStatus(bpm) : (heartData.heart_rate_status || null);
        status = mapStatusToCategory(det);

      } else if (statusFilter === 'bp') {
        const bpData = vitalsObj.bp || {};
        const sysVal = parseInt(bpData.systolic_bp);
        const diaVal = parseInt(bpData.diastolic_bp);
        const sys = isNaN(sysVal) ? null : sysVal;
        const dia = isNaN(diaVal) ? null : diaVal;

        // Fallback logic: Use pre-calculated status if raw value is missing
        const det = (sys && dia) ? calculateBPStatus(sys, dia) : (bpData.bp_status || null);
        status = mapStatusToCategory(det);

      } else if (statusFilter === 'bmi') {
        const bmiData = vitalsObj.bmi || {};
        const bmiVal = parseFloat(bmiData.bmi);
        const val = isNaN(bmiVal) ? null : bmiVal;

        // Fallback logic: Use pre-calculated status if raw value is missing
        const det = val !== null ? calculateBMIStatus(val) : (bmiData.bmi_status || null);
        status = mapStatusToCategory(det);

      } else {
        // Overall: Use the combined healthStatus already calculated in fetchDashboardData
        status = patient.healthStatus || 'normal';
      }

      if (status === 'critical') criticalCount++;
      else if (status === 'warning') warningCount++;
      else normalCount++;
    });

    console.log(`📊 Pie Chart Stats [${statusFilter}]: Critical=${criticalCount}, Warning=${warningCount}, Normal=${normalCount}`);

    const labels = {
      critical: 'Needs Priority Review',
      warning: 'Needs Review',
      normal: 'Within Usual Range'
    };

    const formattedStatusData = [
      { name: labels.critical, value: criticalCount, color: '#f44336' },
      { name: labels.warning, value: warningCount, color: '#ff9800' },
      { name: labels.normal, value: normalCount, color: '#4caf50' }
    ];

    setHealthStatusData(formattedStatusData);

  }, [myPatients, statusFilter, patientsLoading]);

  // Simulate loading state for status distribution when filter changes
  useEffect(() => {
    setStatusLoading(true);
    const timer = setTimeout(() => {
      setStatusLoading(false);
    }, 500); // Simulate a brief loading period
    return () => clearTimeout(timer);
  }, [statusFilter]);

  const handleFilterChange = (event: SelectChangeEvent) => {
    setStatusFilter(event.target.value as string);
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    const fetchPatientsAndAppointments = async () => {
      if (!user?.id) return;

      try {
        setPatientsLoading(true);
        setPatientsError(null);
        const token = localStorage.getItem('token');
        const config = {
          headers: { Authorization: `Bearer ${token}` }
        };

        // Fetch Total Patients Count from /patients endpoint for accuracy
        // Aligning URL with working calls in other pages
        try {
          const allPatientsRes = await axios.get(
            `${process.env.REACT_APP_API_URL}/patients/?assigned_to_me=true`,
            {
              ...config,
              headers: {
                ...config.headers,
                'accept': 'application/json'
              }
            }
          );

          const responseData = allPatientsRes.data;
          let count = 0;

          if (Array.isArray(responseData)) {
            count = responseData.length;
          } else if (responseData && Array.isArray(responseData.patients)) {
            count = responseData.patients.length;
          } else if (responseData && typeof responseData.total === 'number') {
            count = responseData.total;
          } else if (responseData && typeof responseData.count === 'number') {
            count = responseData.count;
          }

          setTotalAssignedPatients(count);
        } catch (err: any) {
          if (err.response && err.response.status === 422) {
          }
        }

        // Fetch Patients/Records with vitals for the charts
        let url = `${process.env.REACT_APP_API_URL}/health/dashboard?limit=100`;
        if (statusFilter && statusFilter !== 'overall') {
          url += `&activity_status=${statusFilter}`;
        }

        const patientsRes = await axios.get(url, config);
        const pData = patientsRes.data;
        const patientsData = Array.isArray(pData) ? pData : (pData.patients || pData.patients_with_data || []);

        const processedPatients = patientsData.map((p: any) => {
          const vitalsObj = p.latest_vitals || {};
          const heartData = vitalsObj.heartrate || vitalsObj.heart_rate || vitalsObj.heartRate || {};
          const bpData = vitalsObj.bp || {};
          const bmiData = vitalsObj.bmi || {};

          const heartRateVal = parseInt(heartData.heart_rate);
          const bpm = isNaN(heartRateVal) ? null : heartRateVal;
          const systolicVal = parseInt(bpData.systolic_bp);
          const sys = isNaN(systolicVal) ? null : systolicVal;
          const diastolicVal = parseInt(bpData.diastolic_bp);
          const dia = isNaN(diastolicVal) ? null : diastolicVal;
          const bmiVal = parseFloat(bmiData.bmi);
          const bmi = isNaN(bmiVal) ? null : bmiVal;

          const heartRateDetail = bpm !== null ? calculateHeartRateStatus(bpm) : (heartData.heart_rate_status || null);
          const bpDetail = (sys && dia) ? calculateBPStatus(sys, dia) : (bpData.bp_status || null);
          const bmiDetail = bmi !== null ? calculateBMIStatus(bmi) : (bmiData.bmi_status || null);

          const hrCat = mapStatusToCategory(heartRateDetail);
          const bpCat = mapStatusToCategory(bpDetail);
          const bmiCat = mapStatusToCategory(bmiDetail);

          let overall: 'critical' | 'warning' | 'normal' = 'normal';
          if ([hrCat, bpCat, bmiCat].includes('critical')) overall = 'critical';
          else if ([hrCat, bpCat, bmiCat].includes('warning')) overall = 'warning';

          return { ...p, healthStatus: overall };
        });

        setMyPatients(processedPatients);
        setCriticalPatients(processedPatients.filter((p: Patient) => p.healthStatus === 'critical'));

        // Fetch Appointments
        const appointmentsRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/appointments/${user.role}/${user.id}`,
          config
        );

        const today = new Date().toISOString().split('T')[0];
        const appointmentsData = appointmentsRes.data.map((apt: any) => {
          const appointmentDateTime = new Date(`${apt.appointment_date}T${apt.appointment_time}`);
          return {
            ...apt,
            id: apt.id,
            patientId: apt.patient_id,
            patientName: apt.patientName || 'Unknown',
            doctorId: apt.doctor_id,
            doctorName: apt.doctorName,
            date: appointmentDateTime,
            time: appointmentDateTime,
            status: apt.status,
            type: apt.appointment_type,
            createdAt: apt.created_at
          };
        });

        setTodayAppointments(appointmentsData.filter((apt: Appointment) =>
          apt.date.toISOString().split('T')[0] === today
        ));

      } catch (error: any) {
        console.error('Error fetching patient data:', error);
        setPatientsError(error.response?.data?.detail || 'Failed to load patient data');
      } finally {
        setPatientsLoading(false);
      }
    };

    fetchPatientsAndAppointments();
  }, [user, statusFilter]);

  useEffect(() => {
    const fetchTrendsData = async () => {
      if (!user?.id) return;

      try {
        setTrendsLoading(true);
        setTrendsError(null);
        const token = localStorage.getItem('token');
        const config = {
          headers: { Authorization: `Bearer ${token}` }
        };

        const trendsRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/dashboard/weekly-health-trends?days=7&activity_status=${trendsFilter}`,
          config
        );

        const trendsRaw = trendsRes.data;
        const trendsData = Array.isArray(trendsRaw) ? trendsRaw : (trendsRaw.trends || []);

        const formattedTrendsData = [];
        for (let i = 6; i >= 0; i--) {
          const date = new Date();
          date.setDate(date.getDate() - i);
          const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

          // Filter matching trends for this date
          const matchingTrends = trendsData.filter((t: any) => {
            if (!t.date) return false;
            const tDate = t.date.includes('T') ? t.date.split('T')[0] : t.date;
            return tDate === dateStr;
          });

          const counts = { critical: 0, warning: 0, normal: 0 };

          // Aggregate all status entries for this date
          matchingTrends.forEach((t: any) => {
            Object.entries(t).forEach(([key, value]) => {
              if (key !== 'date' && typeof value === 'number') {
                const category = mapStatusToCategory(key);
                counts[category] += value;
              }
            });
          });

          formattedTrendsData.push({
            date: dateStr,
            ...counts
          });
        }

        console.log('Weekly Trends Data:', formattedTrendsData);
        setWeeklyTrendsData(formattedTrendsData);
      } catch (error: any) {
        console.error('Error fetching trends data:', error);
        setTrendsError(error.response?.data?.detail || 'Failed to load trends data');
      } finally {
        setTrendsLoading(false);
      }
    };

    fetchTrendsData();
  }, [user, trendsFilter]);

  const handleMarkAsCompleted = async (appointmentId: string) => {
    try {
      setUpdatingAppointment(appointmentId);
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      // Update appointment status to completed
      await axios.put(
        `${process.env.REACT_APP_API_URL}/appointments/${appointmentId}`,
        { status: 'completed' },
        config
      );

      // Update local state
      setTodayAppointments(prevAppointments =>
        prevAppointments.map(apt =>
          apt.id === appointmentId ? { ...apt, status: 'completed' } : apt
        )
      );

      console.log('✅ Appointment marked as completed successfully');
    } catch (error) {
      console.error('❌ Error marking appointment as completed:', error);
      alert('Failed to mark appointment as completed. Please try again.');
    } finally {
      setUpdatingAppointment(null);
    }
  };

  const stats = {
    // Standardize patient count to match admin dashboard logic (from /patients endpoint)
    // Fallback to myPatients deduplicated count if totalAssignedPatients is 0
    totalPatients: totalAssignedPatients || new Set(myPatients.map(p => p.patient_id || p.id)).size,
    todayAppointments: todayAppointments.length,
    pendingAppointments: todayAppointments.filter(a => a.status === 'scheduled').length,
    criticalAlerts: criticalPatients.length
  };

  const getHealthStatusColor = (status: string) => {
    switch (status) {
      case 'critical': return 'error';
      case 'warning': return 'warning';
      case 'normal': return 'success';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Typography variant="h4" gutterBottom>
        Doctor Dashboard
      </Typography>



      {/* Doctor Profile */}
      <DoctorProfileCard
        user={user}
        imageUrl={getCacheBustedImageUrl(user?.profile_image, stableImageTimestamp)}
      />

      {/* Key Metrics */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)'
          },
          gap: 3,
          mb: 4
        }}
      >
        <StatCard
          title="My Patients"
          value={stats.totalPatients}
          icon={<People fontSize="large" />}
          color="primary.main"
          subtitle="Total assigned"
        />
        <StatCard
          title="Today's Appointments"
          value={stats.todayAppointments}
          icon={<CalendarToday fontSize="large" />}
          color="info.main"
          subtitle={`${stats.pendingAppointments} pending`}
        />
        <StatCard
          title="Trends Flagged"
          value={stats.criticalAlerts}
          icon={<Warning fontSize="large" />}
          color="error.main"
          subtitle="Flagged for review"
        />
        <StatCard
          title="Health Monitoring"
          value="Active"
          icon={<MonitorHeart fontSize="large" />}
          color="success.main"
          subtitle="Real-time tracking"
        />
      </Box>

      <Box sx={{
        display: 'grid',
        gap: 3,
        gridTemplateColumns: {
          xs: '1fr',
          md: 'repeat(2, 1fr)'
        }
      }}>
        {/* Today's Appointments */}
        <Box>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Today's Appointments
              </Typography>
              {todayAppointments.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CalendarToday sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    No appointments scheduled for today
                  </Typography>
                </Box>
              ) : (
                <List>
                  {todayAppointments.map((appointment) => (
                    <ListItem key={appointment.id} divider>
                      <ListItemIcon>
                        <AccessTime color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${appointment.time ? appointment.time.toLocaleTimeString() : 'N/A'} - ${appointment.patientName}`}
                        secondary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                            <Chip
                              label={appointment.type}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                            <Chip
                              label={appointment.status}
                              size="small"
                              color={appointment.status === 'completed' ? 'success' : 'default'}
                              variant="outlined"
                            />
                          </Box>
                        }
                        secondaryTypographyProps={{ component: 'div' }}
                      />
                      <Box>
                        <IconButton
                          color="success"
                          size="small"
                          onClick={() => handleMarkAsCompleted(appointment.id)}
                          disabled={appointment.status === 'completed' || updatingAppointment === appointment.id}
                          title={appointment.status === 'completed' ? 'Already completed' : 'Mark as completed'}
                        >
                          {updatingAppointment === appointment.id ? (
                            <CircularProgress size={20} />
                          ) : (
                            <CheckCircle />
                          )}
                        </IconButton>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Patient Health Status Distribution */}
        <Box>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Patient Health Status
                </Typography>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Vital Type</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Vital Type"
                    onChange={handleFilterChange}
                  >
                    <MenuItem value="heartrate">Heart Rate</MenuItem>
                    <MenuItem value="bp">Blood Pressure</MenuItem>
                    <MenuItem value="bmi">BMI Status</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              {statusLoading || patientsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                  <CircularProgress />
                </Box>
              ) : patientsError ? (
                <Alert severity="error" sx={{ my: 2 }}>
                  {patientsError}
                </Alert>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={healthStatusData.every(d => d.value === 0)
                        ? [{ name: 'No Data', value: 1, color: '#e0e0e0' }]
                        : healthStatusData}
                      cx="50%"
                      cy="50%"
                      labelLine={!healthStatusData.every(d => d.value === 0)}
                      label={({ name, value }) =>
                        (value !== undefined && value > 0 && !healthStatusData.every(d => d.value === 0))
                          ? `${name}: ${value}`
                          : (healthStatusData.every(d => d.value === 0) ? 'No Data' : '')
                      }
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {(healthStatusData.every(d => d.value === 0)
                        ? [{ name: 'No Data', value: 1, color: '#e0e0e0' }]
                        : healthStatusData
                      ).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Health Trends */}
        <Box>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Weekly Health Trends
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Tooltip title="Download Bulk Weekly Report">
                    <IconButton
                      onClick={handleBulkDownload}
                      disabled={downloadingReport}
                      size="small"
                      color="primary"
                    >
                      {downloadingReport ? <CircularProgress size={20} /> : <Download />}
                    </IconButton>
                  </Tooltip>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Vital Type</InputLabel>
                    <Select
                      value={trendsFilter}
                      label="Vital Type"
                      onChange={(e: SelectChangeEvent) => setTrendsFilter(e.target.value as string)}
                    >
                      <MenuItem value="heartrate">Heart Rate</MenuItem>
                      <MenuItem value="bp">Blood Pressure</MenuItem>
                      <MenuItem value="bmi">BMI Status</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
              {trendsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                  <CircularProgress />
                </Box>
              ) : trendsError ? (
                <Alert severity="error" sx={{ my: 2 }}>
                  {trendsError}
                </Alert>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={weeklyTrendsData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <RechartsTooltip />
                    <Legend />
                    <Bar dataKey="critical" stackId="a" fill="#f44336" name="Needs Priority Review" />
                    <Bar dataKey="warning" stackId="a" fill="#ff9800" name="Needs Review" />
                    <Bar dataKey="normal" stackId="a" fill="#4caf50" name="Within Usual Range" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
}